import React from 'react';
import { arrayOf, func, number, shape, string } from 'prop-types';

// Components
import { Box, Button, IconButton, Typography } from '@mui/material';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import { MDropdown } from 'common';

// Utils
import { FILTER_ALL } from 'utils/constants';

const ViewFilterDrawer = ({
  resetFilters,
  filterOptions: {
    affiliateListDropdown,
    locations,
    dataCompanyOptions,
    originatorOptions,
    recipientOptions,
    transferDates,
  },
  filters,
  setFilters,
  numberFuturePtos,
  openPtoList,
  setFilterDrawerOpen,
}) => {
  const showClear = Object.keys(filters).some(key => filters[key] !== FILTER_ALL);
  const { affiliate, dataCompany, location, originator, recipient, transferDate } = filters;

  return (
    <Box
      role="presentation"
      sx={{
        display: 'flex',
        justifyContent: 'space-around',
      }}
    >
      <Box
        display="flex"
        flexDirection="column"
        sx={{
          bgcolor: 'surface.white',
          borderRight: '0.1em solid',
          borderRightColor: 'secondary.light',
          height: '100%',
          width: '100%',
        }}
      >
        <Box
          alignItems="center"
          display="flex"
          justifyContent="space-between"
          sx={{
            borderBottom: '0.1em solid',
            borderColor: 'graphic.light',
            // eslint-disable-next-line sort-keys-fix/sort-keys-fix
            height: { xs: 56, md: 48 },
            pb: 0,
            pl: 2,
            pr: 3,
            pt: 0.5,
          }}
        >
          <Typography sx={{ fontSize: 20, mt: '2px' }} variant="h5">
            Filters
          </Typography>
          <IconButton
            onClick={() => setFilterDrawerOpen(false)}
            size="large"
            sx={{
              color: 'secondary.medium',
              m: 'auto',
              mr: -1.5,
              p: 0,
            }}
          >
            <CloseRoundedIcon />
          </IconButton>
        </Box>

        <Box
          sx={{
            pb: 0,
            pt: 2,
            px: 2,
          }}
        >
          {locations.length > 1 && (
            <MDropdown
              id="location-select"
              items={locations}
              label="Location"
              name="location"
              onChange={setFilters}
              value={location}
            />
          )}
          {numberFuturePtos > 0 && (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                pt: 2,
              }}
            >
              <MDropdown
                id="transferDate-select"
                items={transferDates}
                label="Transfer Date"
                name="transferDate"
                onChange={setFilters}
                value={transferDate}
              />
            </Box>
          )}
          {affiliateListDropdown.length > 2 && openPtoList.length > 1 && (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                pt: 2,
              }}
            >
              <MDropdown
                id="affiliate-select"
                items={affiliateListDropdown}
                label="Affiliate"
                name="affiliate"
                onChange={setFilters}
                value={affiliate}
              />
            </Box>
          )}
          {dataCompanyOptions.length > 2 && (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                pt: 2,
              }}
            >
              <MDropdown
                id="system-select"
                items={dataCompanyOptions}
                label="System"
                name="dataCompany"
                onChange={setFilters}
                value={dataCompany}
              />
            </Box>
          )}
          {recipientOptions.length > 2 && (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                pt: 2,
              }}
            >
              <MDropdown
                id="recipient-select"
                items={recipientOptions}
                label="Recipient"
                name="recipient"
                onChange={setFilters}
                value={recipient}
              />
            </Box>
          )}
          {originatorOptions.length > 2 && (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                pt: 2,
              }}
            >
              <MDropdown
                id="originator-select"
                items={originatorOptions}
                label="Originator"
                name="originator"
                onChange={setFilters}
                value={originator}
              />
            </Box>
          )}
        </Box>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            pt: 2,
          }}
        >
          {showClear && (
            <Button aria-label="Clear" onClick={resetFilters} variant="text">
              Clear
            </Button>
          )}
        </Box>
      </Box>
    </Box>
  );
};

ViewFilterDrawer.propTypes = {
  filterOptions: shape({
    affiliateListDropdown: arrayOf(shape({})).isRequired,
    dataCompanyOptions: arrayOf(shape({})).isRequired,
    locations: arrayOf(shape({})).isRequired,
    originatorOptions: arrayOf(shape({})).isRequired,
    recipientOptions: arrayOf(shape({})).isRequired,
    transferDates: arrayOf(shape({})).isRequired,
  }).isRequired,
  filters: shape({
    affiliate: string,
    dataCompany: string,
    location: string,
    originator: string,
    recipient: string,
    transferDate: string,
  }).isRequired,
  numberFuturePtos: number.isRequired,
  openPtoList: arrayOf(shape({})).isRequired,
  resetFilters: func.isRequired,
  setFilterDrawerOpen: func.isRequired,
  setFilters: func.isRequired,
};

export default ViewFilterDrawer;
